import debounce from 'lodash/debounce';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { locale } from '../../../consts';
import { MediaBreakpoint, useBreakpointInfo, useDeviceInfo } from '../../../hooks/useDeviceInfo';
import { useFetchGames } from '../../../hooks/useFetchGames';
import { transformArrayGameData } from '../../../utils/games/games.utils';
import { joinStrings } from '../../../utils/string';
import { RecommendedGameCarousel } from '../../Games';
import { GameCardContainerType } from '../../Games/GameCard/GameCard.types';
import { Game } from '../../Games/games.model';
import { GameSearchInputCarousel } from '../../Games/GameSearch';
import { GamesList } from './GamesList';
import { SearchBarInput } from './SearchBarInput';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './styles';

interface SearchBarProps {
  isUsedOverlay?: boolean;
  children?: JSX.Element;
  closeGameSpinZoneSearch?: () => void;
  onCloseMenu?: () => void;
  isShort?: boolean;
  desktopItems?: number;
  autoFocus?: boolean;
}

/**
 * Search games component which is used to search games.
 */
export const SearchBar: FC<SearchBarProps> = ({
  isShort = false,
  children,
  desktopItems,
  autoFocus = false,
  onCloseMenu,
}) => {
  // TODO: remove onClickCapture on a big refactoring
  // onClickCapture prevents closing searchBar on input or results click (Removed in BZD-42)
  const auth = useAuth();
  const { t } = useTranslations();
  const isMobile = useBreakpointInfo([MediaBreakpoint.BELOW_MOBILE]);
  const { isMobileDevice, isTablet } = useDeviceInfo();
  const recommendedTitle = auth?.isAuthenticated ? t('game.recommended.label') : t('game.featured.label');

  const [query, setQuery] = useState<string>('');
  const [data, setData] = useState<Game[] | null>();
  const [isShortInput, setIsShortInput] = useState<boolean>(isShort);
  const [isSearchBarOpen, setIsSearchBarOpen] = useState<boolean>(false);

  const { searchGame, searchedGameQueryLoading } = useFetchGames({
    onSearchGameComplete: (data) => {
      const transformedData = transformArrayGameData(data.game);

      setData(transformedData);
    },
  });

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);

  async function handleDebounce(inputValue: string): Promise<void> {
    setData(null);

    if (inputValue.trim().length >= 3) {
      await searchGame({
        variables: {
          searchTerm: inputValue,
          language: locale,
        },
      });
    }
  }

  const debouncedRequest = useCallback(debounce(handleDebounce, 500), []);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
    debouncedRequest(event.target.value);
  };

  const onClear = (): void => {
    setQuery('');
    setData(null);
  };

  const onClose = (): void => {
    !isMobile && searchInputRef?.current?.blur();

    onClear();
    onCloseMenu?.();
  };

  const onFocus = (): void => {
    if (!isSearchBarOpen) {
      setIsShortInput(false);
      setIsSearchBarOpen(true);
    }
  };

  const handleClickOutsideSearch = (event: Event): void => {
    const clickInsideSearchBar = (event.target as HTMLElement).closest('.search-bar-container');

    if (!clickInsideSearchBar) {
      setIsSearchBarOpen(false);
      isShort && setIsShortInput(true);
    }
  };

  const searchBarProps = {
    searchInputRef,
    onChange,
    value: query,
    isLoading: searchedGameQueryLoading,
    isOpen: isSearchBarOpen,
    onClose,
    onFocus,
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutsideSearch);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      searchInputRef.current?.focus();
    }
  }, [autoFocus]);

  return (
    <div className={joinStrings(['search-bar-container', isShortInput && 'search-bar-container--short'])}>
      <SearchBarInput theme="light" {...searchBarProps} />
      {isSearchBarOpen && (
        <div className="search-bar-container__results" ref={searchResultsRef}>
          {data && data.length ? (
            isMobileDevice && !isTablet ? (
              <GamesList data={data} simpleMode onClose={onClose} />
            ) : (
              <GameSearchInputCarousel data={data} onClose={onClose} />
            )
          ) : children ? (
            children
          ) : (
            <RecommendedGameCarousel
              title={recommendedTitle}
              containerType={GameCardContainerType.Wide}
              visibleConfig={{
                desktop: 6,
                wide: 6,
                mobile: 2,
                tablet: 4,
              }}
              onClose={onClose}
            />
          )}
        </div>
      )}
    </div>
  );
};
